import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FormControlLabel, InputAdornment, MenuItem, Switch, TextField } from "@mui/material";
import { Download, RefreshCw, Search, Send } from "react-feather";
import { withSuperAdminAuth } from "../../../components/GetAuth";
import PageHeader from "../../../components/PageHeader";
import TablePaginationComponent from "../../../components/TablePagination";
import { downloadFile, formatGBP2 } from "../../../components/Features";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import { ConfirmDialog, fmtDate, MarkPaidDialog, saGet, saPost, StatusChip, TableSkeleton, Toast, useToast } from "../shared";
import styles from "../superadmin.module.css";
import dayjs from "dayjs";

// Last 12 months for the month filter
const MONTHS = Array.from({ length: 12 }).map((_, i) => dayjs().startOf("month").subtract(i, "month"));

const InvoiceList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [payInvoice, setPayInvoice] = useState(null);
  const [voidInvoice, setVoidInvoice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, showMessage, closeToast] = useToast();
  const [search, setSearch] = useState(searchParams.get("search") || "");

  const status = searchParams.get("status") || "all";
  const month = searchParams.get("month") || "";
  const emailFailed = searchParams.get("email_failed") === "1";
  const companyId = searchParams.get("company_id") || "";
  const page = parseInt(searchParams.get("page") || "0", 10);
  const perPage = parseInt(searchParams.get("per_page") || "25", 10);

  const { websiteData } = useWebsite();
  useSiteBranding("Invoices", websiteData);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === "" || value === null || value === "all") next.delete(key); else next.set(key, value);
    if (key !== "page") next.delete("page");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if ((searchParams.get("search") || "") !== search) setParam("search", search);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const load = () => {
    setLoading(true);
    saGet("invoices", { status, month, email_failed: emailFailed ? 1 : 0, company_id: companyId, search: searchParams.get("search") || "", page: page + 1, per_page: perPage }).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") {
        setRows(response.data.data.data);
        setTotal(response.data.data.total);
        setTotals(response.data.data.totals || {});
      }
    }).catch(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [searchParams]);

  const handle = (response) => {
    setBusy(false);
    showMessage(response.data.message, response.data.status === "SUCCESS" ? "success" : "error");
    if (response.data.status === "SUCCESS") load();
  };

  const resend = (inv) => saPost(`invoices/${inv.id}/resend`).then(handle);
  const resendFailed = () => { setBusy(true); saPost("invoices/resend-failed").then(handle).catch(() => setBusy(false)); };
  const doVoid = () => { setBusy(true); saPost(`invoices/${voidInvoice.id}/void`).then((r) => { setVoidInvoice(null); handle(r); }).catch(() => setBusy(false)); };

  return (<div className={`${styles.Page}`}>
    <PageHeader title="Invoices" trail={[{ label: "Billing", to: "/super-admin/revenue" }]} />

    <div className={`${styles.KpiGrid}`}>
      <div className={`${styles.Kpi}`}><span className={`${styles.KpiLabel}`}>Invoiced</span><span className={`${styles.KpiValue}`}>{formatGBP2(totals.invoiced)}</span><span className={`${styles.KpiSub}`}>Matching the filters, excl. void</span></div>
      <div className={`${styles.Kpi}`}><span className={`${styles.KpiLabel}`}>Paid</span><span className={`${styles.KpiValue}`}>{formatGBP2(totals.paid)}</span></div>
      <div className={`${styles.Kpi} ${totals.outstanding > 0 ? styles.KpiDanger : ""}`}><span className={`${styles.KpiLabel}`}>Outstanding</span><span className={`${styles.KpiValue}`}>{formatGBP2(totals.outstanding)}</span></div>
    </div>

    <div className={`${styles.Toolbar}`}>
      <TextField className={`${styles.Search}`} size="small" placeholder="Invoice number or company" value={search} onChange={(e) => setSearch(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><Search size={16} /></InputAdornment>) }} />
      <TextField className={`${styles.Filter}`} size="small" select label="Status" value={status} onChange={(e) => setParam("status", e.target.value)}>
        <MenuItem value="all">All statuses</MenuItem>
        <MenuItem value="pending">Pending</MenuItem>
        <MenuItem value="overdue">Overdue</MenuItem>
        <MenuItem value="paid">Paid</MenuItem>
        <MenuItem value="void">Void</MenuItem>
      </TextField>
      <TextField className={`${styles.Filter}`} size="small" select label="Issued" value={month} onChange={(e) => setParam("month", e.target.value)}>
        <MenuItem value="">Any month</MenuItem>
        {MONTHS.map((m) => <MenuItem key={m.format("YYYY-MM")} value={m.format("YYYY-MM")}>{m.format("MMMM YYYY")}</MenuItem>)}
      </TextField>
      <FormControlLabel control={<Switch checked={emailFailed} onChange={(e) => setParam("email_failed", e.target.checked ? "1" : "")} />} label="Email failed" />
      {companyId && <button type="button" className={`${styles.ActionBU}`} onClick={() => setParam("company_id", "")}>Clear company filter ✕</button>}
      <div className={`${styles.ToolbarRight}`}>
        <button type="button" className={`${styles.ActionBU}`} onClick={resendFailed} disabled={busy}><RefreshCw />Resend all failed emails</button>
      </div>
    </div>

    {loading && <TableSkeleton rows={8} />}

    {!loading && (<div className={`${styles.TableWrap}`}>
      <table className={`${styles.Table}`}>
        <thead>
          <tr><th>Invoice</th><th>Company</th><th>Period</th><th>Due</th><th className={`${styles.Right}`}>Total</th><th>Status</th><th>Emailed</th><th></th></tr>
        </thead>
        <tbody>
          {rows.map((inv) => (<tr key={inv.id}>
            <td><Link to={`/super-admin/invoices/${inv.id}`} className={`${styles.RowLink}`}>{inv.invoice_number}</Link><span className={`${styles.SubText}`}>Issued {fmtDate(inv.issue_date)}</span></td>
            <td><Link to={`/super-admin/companies/${inv.company_id}?tab=invoices`} className={`${styles.RowLink}`}>{inv.company_name}</Link></td>
            <td>{dayjs(inv.period_start).format("DD MMM")} – {fmtDate(inv.period_end)}</td>
            <td>{fmtDate(inv.due_date)}{inv.status === "overdue" && <span className={`${styles.SubText} ${styles.Danger}`}>Suspends after {fmtDate(inv.suspend_on)}</span>}</td>
            <td className={`${styles.Right}`}>{formatGBP2(inv.total)}</td>
            <td><StatusChip status={inv.status} />{inv.status === "paid" && <span className={`${styles.SubText}`}>{fmtDate(inv.paid_at)}</span>}</td>
            <td>{inv.emailed_at ? fmtDate(inv.emailed_at) : <span className={`${styles.Danger}`} title={inv.email_error || ""}>{inv.email_error ? "Failed" : "Not sent"}</span>}</td>
            <td>
              <div className={`${styles.Actions}`}>
                {(inv.status === "pending" || inv.status === "overdue") && <button type="button" className={`${styles.ActionBU} ${styles.ActionPrimary}`} onClick={() => setPayInvoice(inv)}>Mark paid</button>}
                <button type="button" className={`${styles.ActionBU}`} onClick={() => resend(inv)} title="Resend email" aria-label={`Resend ${inv.invoice_number}`}><Send /></button>
                <button type="button" className={`${styles.ActionBU}`} onClick={() => downloadFile(`v1/admin/invoices/${inv.id}/pdf`, `${inv.invoice_number}.pdf`)} title="Download PDF" aria-label={`Download ${inv.invoice_number}`}><Download /></button>
                {inv.status !== "paid" && inv.status !== "void" && <button type="button" className={`${styles.ActionBU} ${styles.ActionDanger}`} onClick={() => setVoidInvoice(inv)}>Void</button>}
              </div>
            </td>
          </tr>))}
          {rows.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", padding: 30 }}>No invoices match these filters.</td></tr>}
        </tbody>
      </table>
      {total > 0 && <div className={`${styles.Pagination}`}>
        <table><tbody><tr>
          <TablePaginationComponent count={total} colSpan={8} page={page} rowsPerPage={perPage} handleChangePage={(e, p) => setParam("page", String(p))} handleChangeRowsPerPage={(e) => setParam("per_page", e.target.value)} />
        </tr></tbody></table>
      </div>}
    </div>)}

    <MarkPaidDialog invoice={payInvoice} onClose={() => setPayInvoice(null)} onDone={load} showMessage={showMessage} />
    <ConfirmDialog open={voidInvoice} busy={busy} title={`Void ${voidInvoice?.invoice_number}?`} message="The invoice is cancelled and no longer counts as owed. If it was the company's only overdue invoice, an overdue suspension is lifted." confirmLabel="Void invoice" onConfirm={doVoid} onClose={() => setVoidInvoice(null)} />
    <Toast toast={toast} onClose={closeToast} />
  </div>);
}

export default withSuperAdminAuth(InvoiceList);
